import React from 'react';
import { Loader2 } from 'lucide-react';
import { organisateurApi, PorteInfo } from '../../../lib/organisateurApi';
import { PagePublique, CartePublique, EnTeteEtablissement, EtatFinal, BOUTON_PLEIN } from '../patron/PagePublique';

const fmtJour = (iso?: string | null) => { if (!iso) return ''; try { return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }); } catch { return ''; } };
const majuscule = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Six cases pour le code (maquette 19a) : une seule vraie saisie, invisible, posée par-dessus les cases
 * — le clavier numérique du téléphone s'ouvre, le collage d'un code entier marche, et la case courante
 * porte l'anneau de focus. Pas six champs à enchaîner à la main.
 */
function CasesCode({ valeur, onChange, desactive, onValider }: { valeur: string; onChange: (v: string) => void; desactive: boolean; onValider: () => void }) {
    const ref = React.useRef<HTMLInputElement>(null);
    const [focus, setFocus] = React.useState(false);
    const courante = Math.min(valeur.length, 5);
    return (
        <div className="relative" onClick={() => ref.current?.focus()}>
            <div className="flex gap-[7px]">
                {Array.from({ length: 6 }).map((_, i) => {
                    const active = focus && i === courante && !desactive;
                    return (
                        <span key={i} aria-hidden className={`flex-1 h-[46px] flex items-center justify-center rounded-[9px] bg-surface border font-mono text-[18px] font-medium tabular-nums text-content ${active ? 'border-content ring-[3px] ring-content/[.07]' : 'border-border'}`}>
                            {valeur[i] || ''}
                        </span>
                    );
                })}
            </div>
            <input ref={ref} type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={6} value={valeur} disabled={desactive}
                aria-label="Votre code à 6 chiffres" autoFocus
                onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={(e) => { if (e.key === 'Enter' && valeur.length === 6) onValider(); }}
                onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
                className="absolute inset-0 w-full h-full opacity-0 cursor-text" />
        </div>
    );
}

/**
 * 19a — la porte. Le dossier concerné, le code, un seul bouton. Et ce que la page dit quand elle ne
 * peut rien faire (19e) : code refusé avec le nombre d'essais restants, blocage 15 minutes, accès
 * expiré ou révoqué. Ni tarif ni information médicale, la page le dit elle-même.
 */
export function OrganisateurPorte({ token, porte, avertissement, onOuvert }: {
    token: string;
    porte: PorteInfo;
    avertissement?: string;
    onOuvert: (session: string) => void;
}) {
    const [code, setCode] = React.useState('');
    const [envoi, setEnvoi] = React.useState(false);
    const [refus, setRefus] = React.useState<{ etat: 'code_refuse' | 'bloque'; restantes?: number; secondes?: number } | null>(
        porte.bloque_secondes ? { etat: 'bloque', secondes: porte.bloque_secondes } : null);

    const etab = porte.etablissement || { name: '', logo_url: null, theme_color: null };
    const bloque = refus?.etat === 'bloque';
    const minutes = Math.max(1, Math.ceil((refus?.secondes || 0) / 60));

    const ouvrir = async () => {
        if (code.length !== 6 || envoi) return;
        setEnvoi(true);
        const r = await organisateurApi.ouvrir(token, code);
        setEnvoi(false);
        if (r.ok && r.data.session) { onOuvert(r.data.session); return; }
        if (r.data.etat === 'bloque') { setRefus({ etat: 'bloque', secondes: r.data.retry_after_sec || 900 }); setCode(''); return; }
        if (r.data.etat === 'code_refuse') { setRefus({ etat: 'code_refuse', restantes: r.data.tentatives_restantes }); setCode(''); return; }
        // Expiré ou révoqué entre-temps : la page se recharge sur le bon état.
        window.location.reload();
    };

    return (
        <PagePublique etablissement={etab} largeur={440}>
            <CartePublique>
                <EnTeteEtablissement etablissement={etab} sousTitre="Suivi des réponses de vos convives" />
                <div className="p-5 flex flex-col gap-[15px]">
                    <div className="px-3.5 py-[13px] rounded-[10px] bg-surface-soft border border-border-soft">
                        <div className="text-[14px] font-bold tracking-[-0.01em] text-content">{porte.dossier?.label}</div>
                        <div className="mt-0.5 text-[12px] text-content-muted">
                            {majuscule(porte.dossier?.date_label || '')}{porte.dossier?.couverts ? ` · ${porte.dossier.couverts} convives annoncés` : ''}
                        </div>
                    </div>

                    {porte.etat === 'expire' && (
                        <EtatFinal ton="attention" titre="Accès expiré" texte={<>Ce lien a expiré le {fmtJour(porte.expires_at)}. Les réponses de vos convives sont conservées.</>} action="Demander un nouvel accès à l'établissement" />
                    )}
                    {porte.etat === 'revoque' && (
                        <EtatFinal ton="attention" titre="Accès révoqué" texte="L'établissement a mis fin à cet accès. Aucune donnée n'a été perdue." action={`Contacter ${etab.name || "l'établissement"}`} />
                    )}

                    {porte.etat === 'ok' && (
                        <>
                            {avertissement && <p className="text-[12px] font-semibold text-state-warn">{avertissement}</p>}
                            <div>
                                <label className="block text-[12px] font-semibold text-content mb-[7px]">Votre code à 6 chiffres</label>
                                <CasesCode valeur={code} onChange={(v) => { setCode(v); if (refus?.etat === 'code_refuse') setRefus(null); }} desactive={bloque || envoi} onValider={ouvrir} />
                                {!refus && <p className="mt-[7px] text-[12px] text-content-muted">Il vous a été envoyé par courriel avec ce lien. Valable jusqu'au {fmtJour(porte.expires_at)}.</p>}
                            </div>
                            {refus?.etat === 'code_refuse' && (
                                <EtatFinal ton="danger" titre="Code refusé" texte={<>Ce code ne correspond pas. Il reste {refus.restantes} tentative{(refus.restantes || 0) > 1 ? 's' : ''} avant blocage temporaire.</>} action="Réessayer" />
                            )}
                            {bloque && (
                                <EtatFinal ton="danger" titre="Trop de tentatives" texte={<>Par sécurité, l'accès est bloqué {minutes} minute{minutes > 1 ? 's' : ''}. Le lien reste valable.</>} action={`Revenir dans ${minutes} minute${minutes > 1 ? 's' : ''}`} />
                            )}
                            <button type="button" onClick={ouvrir} disabled={code.length !== 6 || envoi || bloque} className={BOUTON_PLEIN}>
                                {envoi ? <Loader2 size={14} className="animate-spin" /> : null} Ouvrir le suivi
                            </button>
                            <p className="text-[11.5px] leading-[1.5] text-content-muted text-center text-pretty">
                                Cette page n'affiche ni tarif ni information médicale. Les allergies signalées par vos convives restent entre eux et la cuisine.
                            </p>
                        </>
                    )}
                </div>
            </CartePublique>
        </PagePublique>
    );
}
